import { supabase } from '../../shared/supabase/supabaseClient';
import type { OrderStatus } from '../domain/orderTypes';

export type DeliveryTrackingRow = {
  id: string;
  order_id: string;
  assigned_to: string | null;
  status: 'dispatched' | 'picked_up' | 'in_transit' | 'delivered' | 'failed';
  lat: number | null;
  lng: number | null;
  last_updated_at: string;
};

export async function getDeliveryTracking(orderId: string): Promise<DeliveryTrackingRow | null> {
  const { data, error } = await supabase
    .from('delivery_tracking')
    .select('id, order_id, assigned_to, status, lat, lng, last_updated_at')
    .eq('order_id', orderId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data as DeliveryTrackingRow | null;
}

export async function dispatchOrder(orderId: string, assignedTo?: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('No session');

  const res = await fetch(
    `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/dispatch-customer-order`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ order_id: orderId, assigned_to: assignedTo }),
    },
  );

  const data = (await res.json()) as { ok?: boolean; error?: string };
  if (!res.ok || !data.ok) throw new Error(data.error ?? 'Error al despachar');
}

export async function matchOrderToRider(orderId: string): Promise<{ offers_sent: number; rider_earning: number }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('No session');

  const res = await fetch(
    `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/match-order-to-rider`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ order_id: orderId }),
    },
  );

  const data = (await res.json()) as {
    ok?: boolean;
    error?: string;
    offers_sent?: number;
    rider_earning?: number;
  };
  if (!res.ok || !data.ok) throw new Error(data.error ?? 'Error al buscar repartidores');
  return { offers_sent: data.offers_sent ?? 0, rider_earning: data.rider_earning ?? 0 };
}

export async function fetchAssignmentForOrder(orderId: string): Promise<{
  id: string;
  status: string;
  payout_status: string;
  rider_earning: number;
  rider_id: string | null;
  rider_name?: string | null;
  rider_phone?: string | null;
  rider_rating_avg?: number | null;
  rider_rating_count?: number | null;
} | null> {
  const { data, error } = await supabase
    .from('delivery_assignments')
    .select('id, status, payout_status, rider_earning, rider_id')
    .eq('order_id', orderId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  let rider_name: string | null = null;
  let rider_phone: string | null = null;
  let rider_rating_avg: number | null = null;
  let rider_rating_count: number | null = null;
  if (data.rider_id) {
    const { data: rider } = await supabase
      .from('riders')
      .select('full_name, phone, rating_avg, rating_count')
      .eq('id', data.rider_id)
      .maybeSingle();
    const r = rider as {
      full_name?: string;
      phone?: string;
      rating_avg?: number;
      rating_count?: number;
    } | null;
    rider_name = r?.full_name ?? null;
    rider_phone = r?.phone ?? null;
    rider_rating_avg = r?.rating_avg != null ? Number(r.rating_avg) : null;
    rider_rating_count = r?.rating_count != null ? Number(r.rating_count) : null;
  }
  return { ...data, rider_name, rider_phone, rider_rating_avg, rider_rating_count };
}

export async function getRiderPayoutInfo(assignmentId: string): Promise<{
  amount: number;
  alias: string | null;
  cbu: string | null;
  init_point: string | null;
  rider_name: string | null;
  rider_phone: string | null;
  message?: string;
}> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('No session');

  const res = await fetch(
    `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/create-rider-payout-link`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ assignment_id: assignmentId }),
    },
  );
  const data = (await res.json()) as Record<string, unknown>;
  if (!res.ok || !data.ok) throw new Error(String(data.error ?? 'Error payout'));
  return {
    amount: Number(data.amount ?? 0),
    alias: (data.alias as string) ?? null,
    cbu: (data.cbu as string) ?? null,
    init_point: (data.init_point as string) ?? null,
    rider_name: (data.rider_name as string) ?? null,
    rider_phone: (data.rider_phone as string) ?? null,
    message: data.message as string | undefined,
  };
}

export async function markRiderPayoutPaid(assignmentId: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('No session');

  const res = await fetch(
    `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/confirm-rider-payout`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ assignment_id: assignmentId, action: 'merchant_marked' }),
    },
  );
  const data = (await res.json()) as { ok?: boolean; error?: string };
  if (!res.ok || !data.ok) throw new Error(data.error ?? 'Error al marcar pagado');
}
